import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { UserStats } from "../components/UserStats";
import { toast } from "sonner";
import { EyeOff, User, Loader2 } from "lucide-react";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const { t, translateError } = useLanguage();
  const [anonymous, setAnonymous] = useState(!!user?.anonymous);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/me/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error(t("settings.nameEmpty"));
    setSavingName(true);
    try {
      const { data } = await api.patch("/settings", { name: trimmed });
      setUser({ ...user, name: data.name });
      setName(data.name);
      toast.success(t("settings.nameSaved"));
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSavingName(false);
    }
  };

  const toggle = async (val) => {
    setAnonymous(val);
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", { anonymous: val });
      setUser({ ...user, anonymous: data.anonymous });
      toast.success(val ? t("settings.anonOn") : t("settings.anonOff"));
    } catch (e) {
      setAnonymous(!val);
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="relative z-10 mx-auto max-w-2xl px-6 py-16 md:px-10">
      <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("settings.eyebrow")}</span>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("settings.title")}</h1>

      <h2 className="mt-10 font-heading text-xl font-bold">{t("stats.title")}</h2>
      <div className="mt-4">
        <UserStats stats={stats || user} />
      </div>

      <div className="mt-10 border border-white/10 bg-card p-6">
        <div className="flex gap-4">
          <User className="mt-1 h-6 w-6 shrink-0 text-credit" />
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold">{t("settings.nameTitle")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("settings.nameText")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                placeholder={t("settings.namePlaceholder")}
                data-testid="name-input"
                className="border-white/15 bg-background"
              />
              <Button
                onClick={saveName}
                disabled={savingName || !name.trim() || name.trim() === user?.name}
                data-testid="save-name-button"
                className="shrink-0 bg-primary font-bold uppercase tracking-wider text-primary-foreground"
              >
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : t("settings.saveName")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-card p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex gap-4">
            <EyeOff className="mt-1 h-6 w-6 shrink-0 text-credit" />
            <div>
              <h2 className="font-heading text-xl font-bold">{t("settings.anonTitle")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t("settings.anonText")}</p>
            </div>
          </div>
          <Switch checked={anonymous} onCheckedChange={toggle} disabled={saving} data-testid="anonymous-toggle" />
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-card p-6">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("settings.loggedInAs")}</p>
        <p className="mt-1 font-heading text-lg font-bold">{user?.name}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
      </div>
    </main>
  );
}
